import { Conversations451Api } from '../conversations451';
import { StudentAssistantsApi } from '../student-assistants';
import { WorkflowFormTypes } from '../workflows';

export const AI_WORKFLOW_ENROLLMENTS_DEFAULT_PAGE_SIZE = 50;

export enum AiWorkflowEnrollmentStatus {
  Pending = 'pending',
  Idle = 'idle',
  Thinking = 'thinking',
  TakingAction = 'taking_action',
  WaitingForApproval = 'waiting_for_approval',
  Completed = 'completed',
  Cancelled = 'cancelled',
  Abandoned = 'abandoned'
}

export const AiWorkflowEnrollmentStatusLabels: Record<
  AiWorkflowEnrollmentStatus,
  string
> = {
  [AiWorkflowEnrollmentStatus.Pending]: 'Pending',
  [AiWorkflowEnrollmentStatus.Idle]: 'Monitoring',
  [AiWorkflowEnrollmentStatus.Thinking]: 'Thinking...',
  [AiWorkflowEnrollmentStatus.TakingAction]: 'Taking Action...',
  [AiWorkflowEnrollmentStatus.WaitingForApproval]: 'Approval Needed',
  [AiWorkflowEnrollmentStatus.Completed]: 'Goal Completed',
  [AiWorkflowEnrollmentStatus.Cancelled]: 'Cancelled',
  [AiWorkflowEnrollmentStatus.Abandoned]: 'Abandoned'
};

export const AiWorkflowEnrollmentStatusClass: Record<
  AiWorkflowEnrollmentStatus,
  string
> = {
  [AiWorkflowEnrollmentStatus.Pending]: 'status-pending',
  [AiWorkflowEnrollmentStatus.Idle]: 'status-idle',
  [AiWorkflowEnrollmentStatus.Thinking]: 'status-thinking',
  [AiWorkflowEnrollmentStatus.TakingAction]: 'status-taking-action',
  [AiWorkflowEnrollmentStatus.WaitingForApproval]:
    'status-waiting-for-approval',
  [AiWorkflowEnrollmentStatus.Completed]: 'status-completed',
  [AiWorkflowEnrollmentStatus.Cancelled]: 'status-cancelled',
  [AiWorkflowEnrollmentStatus.Abandoned]: 'status-abandoned'
};

export const AiWorkflowEnrollmentStatusIcon: Record<
  AiWorkflowEnrollmentStatus,
  string
> = {
  [AiWorkflowEnrollmentStatus.Pending]: 'bolt-error-1',
  [AiWorkflowEnrollmentStatus.Idle]: 'bolt-cached',
  [AiWorkflowEnrollmentStatus.Thinking]: 'bolt-bulb',
  [AiWorkflowEnrollmentStatus.TakingAction]: 'bolt-horiz',
  [AiWorkflowEnrollmentStatus.WaitingForApproval]: 'bolt-error-1',
  [AiWorkflowEnrollmentStatus.Completed]: 'bolt-check',
  [AiWorkflowEnrollmentStatus.Cancelled]: 'bolt-cancel',
  [AiWorkflowEnrollmentStatus.Abandoned]: 'bolt-cancel'
};

export enum AiWorkflowGoal {
  InformOrNotify = 'inform_or_notify',
  CaptureLeads = 'capture_leads',
  PayDepositFee = 'pay_deposit_fee',

  ScheduleAppointment = 'schedule_appointment',

  SubmitSurvey = 'submit_survey',

  SignUpForEvent = 'sign_up_for_event',

  StartApplication = 'start_application',
  SubmitApplication = 'submit_application',
  PayApplicationFee = 'pay_application_fee',

  SubmitForm = 'submit_form',
  PayForm = 'pay_form',

  JoinSegment = 'join_segment',
  LeaveSegment = 'leave_segment'
}

export const AiWorkflowGoals = {
  [AiWorkflowGoal.InformOrNotify]: {
    goal: AiWorkflowGoal.InformOrNotify,
    name: 'Inform or Notify',
    description: 'Inform or notify the user through direct communication'
  },
  [AiWorkflowGoal.CaptureLeads]: {
    goal: AiWorkflowGoal.CaptureLeads,
    name: 'Capture Leads',
    description: 'Capture leads from the user'
  },
  [AiWorkflowGoal.SubmitApplication]: {
    goal: AiWorkflowGoal.SubmitApplication,
    name: 'Submit Application',
    description: 'Drive user to submit an application'
  },
  [AiWorkflowGoal.SubmitForm]: {
    goal: AiWorkflowGoal.SubmitForm,
    name: 'Submit Form',
    description: 'Drive user to submit a form'
  },
  [AiWorkflowGoal.StartApplication]: {
    goal: AiWorkflowGoal.StartApplication,
    name: 'Start Application',
    description: 'Drive user to start an application'
  },
  [AiWorkflowGoal.PayForm]: {
    goal: AiWorkflowGoal.PayForm,
    name: 'Pay Form',
    description: 'Drive user to pay on a form'
  },
  [AiWorkflowGoal.PayApplicationFee]: {
    goal: AiWorkflowGoal.PayApplicationFee,
    name: 'Pay Application Fee',
    description: 'Drive user to pay on an application fee'
  },
  [AiWorkflowGoal.PayDepositFee]: {
    goal: AiWorkflowGoal.PayDepositFee,
    name: 'Pay Deposit Fee',
    description: 'Drive user to pay on a deposit fee'
  },
  [AiWorkflowGoal.SignUpForEvent]: {
    goal: AiWorkflowGoal.SignUpForEvent,
    name: 'Sign Up for Event',
    description: 'Drive user to sign up for an event'
  },
  [AiWorkflowGoal.ScheduleAppointment]: {
    goal: AiWorkflowGoal.ScheduleAppointment,
    name: 'Schedule Appointment',
    description: 'Drive user to schedule an appointment'
  },
  [AiWorkflowGoal.SubmitSurvey]: {
    goal: AiWorkflowGoal.SubmitSurvey,
    name: 'Submit Survey',
    description: 'Drive user to submit a survey'
  },
  [AiWorkflowGoal.JoinSegment]: {
    goal: AiWorkflowGoal.JoinSegment,
    name: 'Join Segment',
    description: 'Drive user to join a segment'
  },
  [AiWorkflowGoal.LeaveSegment]: {
    goal: AiWorkflowGoal.LeaveSegment,
    name: 'Leave Segment',
    description: 'Drive user to leave a segment'
  }
};

export interface AiWorkflowGoalContext {
  application_guid?: string;
  term_guid?: string;
  major_guid?: string;
  event_guid?: string;
  form_guid?: string;
  require_follow_up_form?: boolean;
  survey_guid?: string;
  availability_guid?: string;
  availability_category_guid?: string;
  availability_type_guid?: string;
  availability_assignee_id?: string;
  segment_guid?: string;
}

export enum AiWorkflowActionType {
  MakeIntroduction = 'make_introduction',
  PromoteEvent = 'promote_event',
  PromoteApplication = 'promote_application',
  PromoteForm = 'promote_form',
  PromoteSurvey = 'promote_survey',
  ScheduleAppointment = 'schedule_appointment',
  ProvideInformation = 'provide_information',
  AssignToCounselor = 'assign_to_counselor',
  EnrollInWorkflow = 'enroll_in_workflow',
  UpdateField = 'update_field'
}

export enum AiWorkflowPrivilegedActionStatus {
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected',
  FeedbackSent = 'feedback_sent'
}

export interface AiWorkflowActionContext {
  application_guid?: string;
  event_guid?: string;
  form_guid?: string;
  survey_guid?: string;
  availability_guid?: string;
  workflow_guid?: string;
  counselor_ids?: string[];
  field?: string;
  value?: string;
}

export interface AiWorkflowAction {
  _id: string;
  name: string;
  type: AiWorkflowActionType;
  context: AiWorkflowActionContext;
  instructions: string;
  active: boolean;
  allow_self_approval: boolean;
  approval_guidelines: string[];
  index_weight: number;
  created_at: string;
  updated_at: string;
  run_at?: string;
}

export const AiWorkflowActions = {
  [AiWorkflowActionType.MakeIntroduction]: {
    action: AiWorkflowActionType.MakeIntroduction,
    name: 'Make Introduction',
    description: 'Reach out to new contact',
    icon: 'bolt-identities'
  },
  [AiWorkflowActionType.PromoteEvent]: {
    action: AiWorkflowActionType.PromoteEvent,
    name: 'Promote Event',
    description: 'Drive to register for event',
    icon: 'bolt-schools'
  },
  [AiWorkflowActionType.PromoteApplication]: {
    action: AiWorkflowActionType.PromoteApplication,
    name: 'Promote Application',
    description: 'Drive to apply',
    icon: 'bolt-applications'
  },
  [AiWorkflowActionType.PromoteForm]: {
    action: AiWorkflowActionType.PromoteForm,
    name: 'Promote Form',
    description: 'Drive to fill out form',
    icon: 'bolt-form-submissions'
  },
  [AiWorkflowActionType.PromoteSurvey]: {
    action: AiWorkflowActionType.PromoteSurvey,
    name: 'Promote Survey',
    description: 'Drive to fill out survey',
    icon: 'bolt-surveys'
  },
  [AiWorkflowActionType.ScheduleAppointment]: {
    action: AiWorkflowActionType.ScheduleAppointment,
    name: 'Schedule Appointment',
    description: 'Application Data',
    icon: 'bolt-appointments'
  },
  [AiWorkflowActionType.ProvideInformation]: {
    action: AiWorkflowActionType.ProvideInformation,
    name: 'Provide Information',
    description: 'Nurture existing contact',
    icon: 'bolt-visibility-groups'
  },
  [AiWorkflowActionType.AssignToCounselor]: {
    action: AiWorkflowActionType.AssignToCounselor,
    name: 'Assign to Counselor',
    description: 'Ensure internal alignment',
    icon: 'bolt-workflows'
  },
  [AiWorkflowActionType.EnrollInWorkflow]: {
    action: AiWorkflowActionType.EnrollInWorkflow,
    name: 'Enroll in Workflow',
    description: 'Connect to non-AI workflows',
    icon: 'bolt-gpa'
  },
  [AiWorkflowActionType.UpdateField]: {
    action: AiWorkflowActionType.UpdateField,
    name: 'Update Field',
    description: 'Make changes based on activity',
    icon: 'segments-1'
  }
} as const;

export interface AiWorkflow {
  guid: string;
  name: string;
  description: string;
  max_enrollments: number | null;
  instructions: string;
  bolt_assistant: string;
  active: boolean;
  goal: AiWorkflowGoal;
  past_activities_enabled: boolean;
  goal_context?: AiWorkflowGoalContext;
  actions: AiWorkflowAction[];
  approvers: string[];
  enrolled_segment: string;
  urgent: boolean;
  deadline: string | null;
  created_at: string;
  updated_at: string;
  metadata?: AiWorkflowMetadata;
  triggers?: AiWorkflowTrigger[];
}

export interface AiWorkflowExpanded extends Omit<AiWorkflow, 'bolt_assistant'> {
  bolt_assistant:
    | StudentAssistantsApi.StudentAssistant
    | StudentAssistantsApi.DefaultStudentAssistant;
}

export interface AiWorkflowMetadata {
  totalPeople?: number;
  approvalNeeded?: number;
  lastActionDate?: Date;
}

export interface AiWorkflowMetadataDto extends AiWorkflowMetadata {
  guid: string;
}

export type AiWorkflowActionDto = Omit<
  AiWorkflowAction,
  '_id' | 'created_at' | 'updated_at' | 'index_weight'
>;

export interface CreateAiWorkflowDto
  extends Omit<
    AiWorkflow,
    | 'guid'
    | '_subdom'
    | 'created_at'
    | 'updated_at'
    | 'actions'
    | 'metadata'
    | 'triggers'
  > {
  actions: AiWorkflowActionDto[];
  triggers: AiWorkflowTriggerDto[];
}

export type UpdateAiWorkflowDto = Partial<
  Omit<CreateAiWorkflowDto, 'actions' | 'enrolled_segment' | 'triggers'>
>;

export interface ReorderAiWorkflowActionsDto {
  ids: string[];
}

export enum NoteCreatorType {
  User = 'user',
  Assistant = 'assistant'
}

export interface AiWorkflowEnrollmentAssessmentNote {
  _id: string;
  note: string;
  created_by: string;
  creator_type: NoteCreatorType;
  created_at: string;
  updated_at: string;
}

export interface Error {
  node: string;
  assessmentId: string | null;
  error: string;
  created_at: string;
}

export type ErrorDto = Omit<Error, 'created_at'>;

export interface AssessmentContentBase {
  reasoning?: string;
}

export interface CommunicationAssessmentContent extends AssessmentContentBase {
  channels: Conversations451Api.DeliveryMethod[];
  subject?: string;
  text: string;
  scheduled_at?: Date;
  conversation_id?: string;

  // Its added separately to hack in multi channel actions.. we should probably refactor this
  email?: string;
  sms?: string;
  phone?: string;
}

export interface AssignToCounselorAssessmentContent
  extends AssessmentContentBase {
  counselorId: string;
}

export interface EnrollInWorkflowAssessmentContent
  extends AssessmentContentBase {
  workflowGuid: string;
}

export interface UpdateFieldAssessmentContent extends AssessmentContentBase {
  field: string;
  value: string;
}

export type AssessmentContent =
  | CommunicationAssessmentContent
  | AssignToCounselorAssessmentContent
  | EnrollInWorkflowAssessmentContent
  | UpdateFieldAssessmentContent;

export enum AiWorkflowEnrollmentAssessmentExecutionResourceType {
  Conversation = 'conversation',
  Task = 'task'
}

export interface AiWorkflowEnrollmentAssessmentExecution {
  resource_guid?: string;
  resource_type: AiWorkflowEnrollmentAssessmentExecutionResourceType;
  executed_at: string;
  scheduled_at?: string;

  // This was added to hack in multi channel actions for the demo.. we should probably refactor this
  email_conversation_guid?: string;
  sms_conversation_guid?: string;
  phone_conversation_guid?: string;
}

export interface AiWorkflowEnrollmentAssessment {
  _id: string;
  actionId: string;
  notes: AiWorkflowEnrollmentAssessmentNote[];
  approval_status: AiWorkflowPrivilegedActionStatus | null;
  content: AssessmentContent;
  error?: Error[];
  execution?: AiWorkflowEnrollmentAssessmentExecution;
  created_at: string;
  updated_at: string;
}

export interface AiWorkflowEnrollmentAssessmentExpanded
  extends Omit<AiWorkflowEnrollmentAssessment, 'actionId'> {
  action: AiWorkflowAction;
}

export type AiWorkflowEnrollmentAssessmentExpandedDto = Omit<
  AiWorkflowEnrollmentAssessmentExpanded,
  '_id' | 'created_at' | 'updated_at'
>;

export type AiWorkflowEnrollmentAssessmentDto = Omit<
  AiWorkflowEnrollmentAssessment,
  '_id' | 'created_at' | 'updated_at'
>;

export interface AiWorkflowEnrollment {
  _subdom: string;
  guid: string;
  user_id: string;
  workflow_guid: string;
  status: AiWorkflowEnrollmentStatus;
  assessments: AiWorkflowEnrollmentAssessment[];
  error: Error[];
  created_at: string;
  updated_at: string;
  wake_time: string;
}

export interface UserDto {
  id: string; // ObjectID hex
  first_name: string;
  last_name: string;
  preferred_name: string;
}

export interface AiWorkflowEnrollmentExpanded
  extends Omit<
    AiWorkflowEnrollment,
    'assessments' | 'user_id' | '_subdom' | 'workflow_guid'
  > {
  assessments: AiWorkflowEnrollmentAssessmentExpanded[];
  user: UserDto;
  workflow: AiWorkflow;
}

export interface EnrollPeopleResponse {
  total: number;
  enrollments: AiWorkflowEnrollmentExpanded[];
}

export interface GetAiWorkflowEnrollmentsParams {
  offset?: number;
  limit?: number;
}

export interface AiWorkflowsFilters {
  active: boolean | null;
  goal: AiWorkflowGoal | null;
}

export interface AiWorkflowsEnrollmentsFilters {
  status: AiWorkflowEnrollmentStatus | null;
  user_ids: string[] | null;
}

export interface ApprovalQueue {
  total: number;
  enrollments: AiWorkflowEnrollmentExpanded[];
}

export interface AiWorkflowEnrollmentsStats {
  abandoned: number;
  cancelled: number;
  completed: number;
  idle: number;
  taking_action: number;
  thinking: number;
  total: number;
  waiting_for_approval: number;
}

export interface WorkflowsStats {
  active: number;
  goalsCompleted: number;
  hoursSaved: number;
}

export interface AiWorkflowTrigger {
  _id: string;
  name: string;
  type: AiWorkflowTriggerType;
  parameters: AiWorkflowTriggerParameters;
  description?: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export type AiWorkflowTriggerParameters = WorkflowFormTypes.TriggerParameters;

export const AiWorkflowTriggerKey = WorkflowFormTypes.TriggerType;

export type AiWorkflowTriggerType =
  (typeof AiWorkflowTriggerKey)[keyof typeof AiWorkflowTriggerKey];

export type AiWorkflowTriggerDto = Omit<
  AiWorkflowTrigger,
  '_id' | 'created_at' | 'updated_at'
>;

export enum TriggerGroup {
  Applications = 'applications',
  Appointments = 'appointments',
  Decisions = 'decisions',
  Documents = 'documents',
  Events = 'events',
  Forms = 'forms',
  Users = 'users',
  Labels = 'labels',
  Conversations = 'conversations'
}

export interface TriggerConfig {
  key: AiWorkflowTriggerType;
  name: string;
  description: string;
  group: string;
  icon: string;
}

export const AiWorkflowTriggers: TriggerConfig[] = [
  {
    name: 'Application Completed',
    description: 'Enroll people who complete an application',
    group: TriggerGroup.Applications,
    key: AiWorkflowTriggerKey.ApplicationCompleted,
    icon: 'bolt-check-circle-outline'
  },
  {
    name: 'Application Started',
    description: 'Enroll people who start an application',
    group: TriggerGroup.Applications,
    key: AiWorkflowTriggerKey.ApplicationStarted,
    icon: 'bolt-applications'
  },
  {
    name: 'Application Submitted',
    description: 'Enroll people who submit an application',
    group: TriggerGroup.Applications,
    key: AiWorkflowTriggerKey.ApplicationSubmitted,
    icon: 'bolt-completed'
  },
  // {
  //   name: 'Payment Submitted',
  //   description: 'Enroll people who pay an application fee',
  //   group: TriggerGroup.Applications,
  //   key: AiWorkflowTriggerKey.ApplicationPaymentSubmitted,
  //   icon: 'bolt-payments'
  // },
  {
    name: 'Recommendation Submitted',
    description: 'Enroll people when a recommendation is submitted',
    group: TriggerGroup.Applications,
    key: AiWorkflowTriggerKey.ApplicationRecommendationSubmitted,
    icon: 'file-check-2'
  },
  {
    name: 'Appointment Attended',
    description: 'Enroll people who have attended an appointment',
    group: TriggerGroup.Appointments,
    key: AiWorkflowTriggerKey.AppointmentAttended,
    icon: 'bolt-appointments'
  },
  {
    name: 'Appointment Canceled',
    description: 'Enroll people who have canceled an appointment',
    group: TriggerGroup.Appointments,
    key: AiWorkflowTriggerKey.AppointmentCanceled,
    icon: 'bolt-appointments'
  },
  {
    name: 'Appointment No Show',
    description:
      'Enroll people who have been marked as No Show to an appointment',
    group: TriggerGroup.Appointments,
    key: AiWorkflowTriggerKey.AppointmentNoShow,
    icon: 'bolt-appointments'
  },
  {
    name: 'Appointment Scheduled',
    description: 'Enroll people who scheduled an appointment',
    group: TriggerGroup.Appointments,
    key: AiWorkflowTriggerKey.AppointmentScheduled,
    icon: 'bolt-appointments'
  },
  {
    name: 'Appointment Updated',
    description: 'Enroll people who Updated an appointment',
    group: TriggerGroup.Appointments,
    key: AiWorkflowTriggerKey.AppointmentUpdated,
    icon: 'bolt-appointments'
  },
  {
    name: 'Conversation Started',
    description: 'Enroll people who start a conversation',
    group: TriggerGroup.Conversations,
    key: AiWorkflowTriggerKey.ConversationStarted,
    icon: 'bolt-conversations'
  },
  {
    name: 'Decision Checklist Items',
    description:
      'Enroll people when their checklist item status (completed, waived, incomplete) is changed',
    group: TriggerGroup.Decisions,
    key: AiWorkflowTriggerKey.DecisionChecklistUpdated,
    icon: 'bolt-check-circle-outline'
  },
  {
    name: 'Decision Released',
    description: 'Enroll people when their application decision is released',
    group: TriggerGroup.Decisions,
    key: AiWorkflowTriggerKey.DecisionReleased,
    icon: 'bolt-check-circle-outline'
  },
  {
    name: 'Decision Stage Changed',
    description: 'Enroll people when their application decision stage changes',
    group: TriggerGroup.Decisions,
    key: AiWorkflowTriggerKey.DecisionStageChanged,
    icon: 'bolt-pencil'
  },
  {
    name: 'Decision Status Changed',
    description: 'Enroll people when their application decision status changes',
    group: TriggerGroup.Decisions,
    key: AiWorkflowTriggerKey.DecisionStatusChanged,
    icon: 'bolt-pencil'
  },
  {
    name: 'Document Uploaded',
    description:
      'Enroll people when a related document is uploaded or imported.',
    group: TriggerGroup.Documents,
    key: AiWorkflowTriggerKey.DocumentUpload,
    icon: 'bolt-documents'
  },
  {
    name: 'Event Signup',
    description: 'Enroll people who sign up for an event',
    group: TriggerGroup.Events,
    key: AiWorkflowTriggerKey.EventSignup,
    icon: 'bolt-events'
  },
  {
    name: 'Form Submitted',
    description:
      'Enroll people who submit a form (e.g. information request on a landing page)',
    group: TriggerGroup.Forms,
    key: AiWorkflowTriggerKey.FormSubmitted,
    icon: 'bolt-form-submissions'
  },
  {
    name: 'Joined Segment',
    description: 'Enroll people when they join a calculated segment',
    group: TriggerGroup.Users,
    key: AiWorkflowTriggerKey.EnteredSegment,
    icon: 'bolt-segments'
  },
  {
    name: 'Joined or Left Segment',
    description: 'Enroll people when they join or exit a calculated segment',
    group: TriggerGroup.Users,
    key: AiWorkflowTriggerKey.EnteredOrExitedSegment,
    icon: 'bolt-segments'
  },
  {
    name: 'Left Segment',
    description: 'Enroll people when they exit a calculated segment',
    group: TriggerGroup.Users,
    key: AiWorkflowTriggerKey.ExitedSegment,
    icon: 'bolt-segments'
  },
  {
    name: 'User Birthday',
    description: 'Enroll people at 5:01 UTC on their birthday',
    group: TriggerGroup.Users,
    key: AiWorkflowTriggerKey.UserBirthday,
    icon: 'bolt-date-range'
  },
  {
    name: 'User Territory Changed',
    description: 'Enroll people when their territory changes',
    group: TriggerGroup.Users,
    key: AiWorkflowTriggerKey.UserTerritoryChanged,
    icon: 'bolt-territories'
  },
  {
    name: 'User Label Added',
    description: 'Enroll people when they are given the selected label',
    group: TriggerGroup.Labels,
    key: AiWorkflowTriggerKey.UserLabelAdded,
    icon: 'bookmark-add'
  },
  {
    name: 'User Label Removed',
    description: 'Enroll people when the selected label is removed from them',
    group: TriggerGroup.Labels,
    key: AiWorkflowTriggerKey.UserLabelRemoved,
    icon: 'bookmark-subtract'
  }
];

export const AiWorkflowTriggersMap = AiWorkflowTriggers.reduce(
  (acc, trigger) => {
    acc[trigger.key] = trigger;
    return acc;
  },
  {} as Record<AiWorkflowTriggerType, TriggerConfig>
);

export enum SocketEvents {
  EnrollmentCreated = 'enrollment-created',
  EnrollmentsCreated = 'enrollments-created',
  EnrollmentAssessmentApproved = 'enrollment-assessment-approved',
  EnrollmentAssessmentRejected = 'enrollment-assessment-rejected',
  EnrollmentAssessmentUpdated = 'enrollment-assessment-updated',
  EnrollmentStatusUpdated = 'enrollment-status-updated',
  EnrollmentAssessmentCreated = 'enrollment-assessment-created'
}

export interface ApprovalQueueEnrollment
  extends Omit<AiWorkflowEnrollmentExpanded, 'assessments'> {
  assessments: AiWorkflowEnrollmentAssessmentExpanded;
}

export interface ApprovalQueueCursor {
  enrollments: ApprovalQueueEnrollment[];
  selectedIndex: number | null;
  total: number;
}

export enum ActionExecutionType {
  UpdateField = 'Update Field',
  EnrollInWorkflow = 'Enroll in Workflow',
  AssignToCounselor = 'Assign to Counselor',
  SendCommunication = 'Send Communication'
}

export function getActionExecutionType(action: AiWorkflowAction) {
  switch (action.type) {
    case AiWorkflowActionType.UpdateField:
      return ActionExecutionType.UpdateField;
    case AiWorkflowActionType.EnrollInWorkflow:
      return ActionExecutionType.EnrollInWorkflow;
    case AiWorkflowActionType.AssignToCounselor:
      return ActionExecutionType.AssignToCounselor;
    case AiWorkflowActionType.MakeIntroduction:
    case AiWorkflowActionType.PromoteEvent:
    case AiWorkflowActionType.PromoteApplication:
    case AiWorkflowActionType.PromoteForm:
    case AiWorkflowActionType.PromoteSurvey:
    case AiWorkflowActionType.ScheduleAppointment:
    case AiWorkflowActionType.ProvideInformation:
      return ActionExecutionType.SendCommunication;
    default:
      return null;
  }
}
